/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import ContentFrame from '../../applicationFrame/ContentFrame';
import { ScrollAwareScroller } from '../../components/ScrollAware';
import ContentWrapper from '../../components/ContentWrapper';

import HealthEntryIcons from '../../components/icons/HealthEntryIcons';
import IconMeasurements from '../../components/icons/IconMeasurements';
import ZeroStateGenetics from '../../components/icons/ZeroStateGenetics';
import AppNavHealthEntryScrollable from '../../appAuthorized/presenters/AppNavHealthEntryScrollable';

import HealthEntryCategoryNames from '../../../constants/HealthEntryCategoryNames';

import HEShareCards from './components/HEShareCards';
import HEValueSeries from './components/HEValueSeries';
import HealthEntryFooter from './components/HealthEntryFooter';
import { useHealthEntryForActiveProfile, useHealthEntryValuesForActiveProfile } from '../../hooks/useHealthEntry';
import { useNavigate } from '../../hooks/useNavigate';
import RouteNames from '../../appAuthorized/RouteNames';
import { useActiveUser } from '../../hooks/useUser';
import ContentLoader from '../../components/ContentLoader';
import { useShareCardListForActiveProfile } from '../../hooks/useShareCard';
import { useLists } from '../../hooks/useLists';

interface Props {
  healthEntryId: number;
  isNew: boolean;
}

const HealthEntryOccurrenceList: React.FC<Props> = (props) => {
  const { healthEntryId, isNew } = props;

  let EmptyIcon = null;

  const [{ healthEntry, category, ...heData }, heActions] = useHealthEntryForActiveProfile(healthEntryId);
  const [, hevActions] = useHealthEntryValuesForActiveProfile();

  const [, navActions] = useNavigate();
  const [{ preferredLanguageCode }] = useActiveUser();
  const [scData] = useShareCardListForActiveProfile();
  const [listData] = useLists();
  const isLoaded = scData.isLoaded && heData.isLoaded && listData.isLoaded;

  switch (category) {
    case HealthEntryCategoryNames.lab:
      EmptyIcon = HealthEntryIcons.IconLab;
      break;
    case HealthEntryCategoryNames.measurement:
      EmptyIcon = IconMeasurements;
      break;
    case HealthEntryCategoryNames.geneticResult:
      EmptyIcon = ZeroStateGenetics;
      break;
    default:
      break;
  }

  return (
    <ContentFrame className="bkgd-color-light-neutral">
      <ScrollAwareScroller>

        <AppNavHealthEntryScrollable
          healthEntryId={healthEntryId}
          deletePhraseId="heSummary.deleteConfirm.button.label"
        />

        <ContentWrapper>
          <ContentLoader isContentLoaded={isLoaded} blockRenderUntilLoaded={true} isFixed={true}>
            <HEValueSeries
              healthEntry={healthEntry}
              EmptyIcon={EmptyIcon}
              onClickItem={(heValue) => {
                navActions.goTo(RouteNames.healthEntryValue(healthEntryId, heValue.id));
              }}
              onClickAddValue={() => {
                heActions.apiAddHealthEntryValue()
                .then(({ newId, pendingHeValue }) => {
                  let destinationUrl = '/';
                  const he = heActions.instantiateHealthEntry();
                  const hasValues = he.values.hasValues();
                  const mostRecentValue = he.values.mostRecent();

                  if (hasValues && mostRecentValue.amount && mostRecentValue.amount.hasAmount()) {
                    const unit = mostRecentValue.amount.getUnit();
                    pendingHeValue.amount.setUnit(unit);
                    hevActions.updateEditing(pendingHeValue.data());
                  }

                  if (pendingHeValue.amount) {
                    destinationUrl = hasValues
                      ? RouteNames.healthEntryValueAmount(healthEntryId, newId)
                      : RouteNames.healthEntryValue(healthEntryId, newId);
                  } else if (pendingHeValue.geneVariantResult) {
                    destinationUrl = hasValues
                      ? RouteNames.healthEntryValueGeneVariant(healthEntryId, newId)
                      : RouteNames.healthEntryValue(healthEntryId, newId);
                  }

                  navActions.goTo(destinationUrl);
                });
              }}
              languageCode={preferredLanguageCode}
            />

            <HEShareCards
              healthEntry={healthEntry}
              allowSettingIncludedValues={true}
              shareCardsDefaultExpanded={isNew}
            />

            <HealthEntryFooter
              onDelete={() => {
                heActions.apiDeleteHealthEntry().then(() => {
                  navActions.goTo(RouteNames.myBackpack());
                });
              }}
              onToggleFlag={null}
              primaryButtonPhrase={{
                id: 'heSummary.deleteConfirm.button.label',
              }}
              healthEntry={healthEntry}
              healthEntryValue={null}
            />

          </ContentLoader>
        </ContentWrapper>
      </ScrollAwareScroller>
    </ContentFrame>
  );
};

export default HealthEntryOccurrenceList;
